import { useEffect, useRef, useState } from "react";

type Credentials = {
  email: string;
  athleteSlug: string;
  availableAthleteSlugs: string[];
  intervalsIcuAthleteId: string;
  hasApiKey: boolean;
  hasGoogleToken: boolean;
  googleCalendarIds: string[];
  googleWriteCalendarId: string;
};

type Calendar = {
  id: string;
  name: string;
  primary: boolean;
  accessRole: string;
};

// Banner copy keyed by the ?gcal=… status the OAuth callback redirects with.
const GCAL_STATUS: Record<string, { ok: boolean; text: string }> = {
  connected: { ok: true, text: "Google Calendar connected." },
  denied: { ok: false, text: "Google Calendar access was denied." },
  norefresh: {
    ok: false,
    text: "Google didn't return a refresh token. Try connecting again.",
  },
  needintervals: {
    ok: false,
    text: "Set up intervals.icu first, then connect Google Calendar.",
  },
  error: { ok: false, text: "Connecting Google Calendar failed. Try again." },
};

// Mirrors the former server-rendered settings page. Auth is enforced
// client-side: a 401 from /api/me/credentials bounces to /login, matching the
// old server redirect.
export function SettingsPage() {
  const [who, setWho] = useState("Loading…");
  const [athleteSlug, setAthleteSlug] = useState("");
  const [availableSlugs, setAvailableSlugs] = useState<string[]>([]);
  const [athleteId, setAthleteId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [keyPlaceholder, setKeyPlaceholder] = useState("paste your API key");
  const [message, setMessage] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  // Google Calendar state.
  const [googleConnected, setGoogleConnected] = useState(false);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [calError, setCalError] = useState("");
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [writeId, setWriteId] = useState("");
  const [calMessage, setCalMessage] = useState("");
  const [calOk, setCalOk] = useState(false);
  const [calBusy, setCalBusy] = useState(false);
  const loaded = useRef(false);

  async function load() {
    const res = await fetch("/api/me/credentials", { credentials: "include" });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    const d = (await res.json()) as Credentials;
    setWho(d.email ? "Signed in as " + d.email : "");
    setAthleteSlug(d.athleteSlug || "");
    setAvailableSlugs(d.availableAthleteSlugs || []);
    setAthleteId(d.intervalsIcuAthleteId || "");
    setKeyPlaceholder(
      d.hasApiKey ? "•••••••• (set — blank keeps it)" : "paste your API key",
    );
    setGoogleConnected(d.hasGoogleToken);
    setReadIds(new Set(d.googleCalendarIds || []));
    setWriteId(d.googleWriteCalendarId || "");
    if (d.hasGoogleToken) void loadCalendars();
  }

  async function loadCalendars() {
    const res = await fetch("/api/google-calendar/calendars", {
      credentials: "include",
    });
    if (!res.ok) return;
    const d = (await res.json()) as {
      connected: boolean;
      calendars: Calendar[];
      error?: string;
    };
    setCalendars(d.calendars || []);
    setCalError(d.error || "");
  }

  useEffect(() => {
    document.title = "Settings · ultracoach";
    // Surface the OAuth callback result, then strip the query so a refresh
    // doesn't re-show the banner.
    const params = new URLSearchParams(window.location.search);
    const status = params.get("gcal");
    if (status && GCAL_STATUS[status]) {
      setCalOk(GCAL_STATUS[status].ok);
      setCalMessage(GCAL_STATUS[status].text);
      window.history.replaceState({}, "", "/settings");
    }
    // StrictMode double-invokes effects in dev; load once.
    if (loaded.current) return;
    loaded.current = true;
    void load();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage("");
    setOk(false);
    setBusy(true);
    try {
      const res = await fetch("/api/me/credentials", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          athleteSlug,
          intervalsIcuAthleteId: athleteId.trim(),
          intervalsIcuApiKey: apiKey,
        }),
      });
      const d = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        setMessage(d.message || "Save failed");
        setBusy(false);
        return;
      }
      setOk(true);
      setMessage("Saved.");
      setBusy(false);
      setApiKey("");
      void load();
    } catch {
      setMessage("Network error");
      setBusy(false);
    }
  }

  function toggleRead(id: string) {
    setReadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Dropping a calendar from reads also drops it as the write target.
        if (writeId === id) setWriteId("");
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function saveCalendars() {
    setCalMessage("");
    setCalOk(false);
    setCalBusy(true);
    try {
      const res = await fetch("/api/google-calendar/selection", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          googleCalendarIds: [...readIds],
          googleWriteCalendarId: writeId,
        }),
      });
      const d = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        setCalMessage(d.message || "Save failed");
        setCalBusy(false);
        return;
      }
      setCalOk(true);
      setCalMessage("Calendar selection saved.");
      setCalBusy(false);
    } catch {
      setCalMessage("Network error");
      setCalBusy(false);
    }
  }

  async function disconnectGoogle() {
    setCalBusy(true);
    await fetch("/api/google-calendar/disconnect", {
      method: "POST",
      credentials: "include",
    });
    setGoogleConnected(false);
    setCalendars([]);
    setReadIds(new Set());
    setWriteId("");
    setCalOk(true);
    setCalMessage("Google Calendar disconnected.");
    setCalBusy(false);
  }

  async function signOut() {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
    });
    window.location.href = "/login";
  }

  return (
    <>
      <div className="mark">
        <i />
        <i />
        <i />
        <i />
      </div>
      <h1>
        Your <span className="brand">athlete</span> setup
      </h1>
      <p className="sub">{who}</p>
      <form onSubmit={onSubmit}>
        <label htmlFor="slug">Coaching athlete</label>
        <select
          id="slug"
          value={athleteSlug}
          onChange={(e) => setAthleteSlug(e.target.value)}
        >
          <option value="">— not linked —</option>
          {availableSlugs.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label htmlFor="aid">intervals.icu athlete ID</label>
        <input
          id="aid"
          type="text"
          placeholder="i123456"
          autoComplete="off"
          value={athleteId}
          onChange={(e) => setAthleteId(e.target.value)}
        />
        <label htmlFor="key">intervals.icu API key</label>
        <input
          id="key"
          type="password"
          placeholder={keyPlaceholder}
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
        <button type="submit" disabled={busy}>
          Save
        </button>
        <div className="err" style={ok ? { color: "#c4f23c" } : undefined}>
          {message}
        </div>
      </form>

      <h2 style={{ marginTop: "2rem" }}>Google Calendar</h2>
      {!googleConnected ? (
        <>
          <p className="sub">
            Connect your Google account to read your calendars. You choose which
            ones to read and which single calendar workouts may be written to.
          </p>
          <a className="button" href="/api/google-calendar/connect">
            Connect Google Calendar
          </a>
        </>
      ) : (
        <>
          <p className="sub">
            Tick the calendars to read. Pick the one personal calendar workouts
            may be written to — nothing is ever written to the others.
          </p>
          {calError ? (
            <div className="err">Couldn't load calendars: {calError}</div>
          ) : calendars.length === 0 ? (
            <p className="sub">Loading calendars…</p>
          ) : (
            <table className="cal-table">
              <thead>
                <tr>
                  <th>Read</th>
                  <th>Calendar</th>
                  <th>Write</th>
                </tr>
              </thead>
              <tbody>
                {calendars.map((cal) => {
                  const isRead = readIds.has(cal.id);
                  const canWrite =
                    cal.accessRole === "owner" || cal.accessRole === "writer";
                  return (
                    <tr key={cal.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isRead}
                          onChange={() => toggleRead(cal.id)}
                          aria-label={`Read ${cal.name}`}
                        />
                      </td>
                      <td>
                        {cal.name}
                        {cal.primary ? (
                          <span style={{ color: "#8b93a3" }}> (primary)</span>
                        ) : null}
                        {!canWrite ? (
                          <span style={{ color: "#8b93a3" }}> · read-only</span>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="radio"
                          name="writeCalendar"
                          checked={writeId === cal.id}
                          disabled={!isRead || !canWrite}
                          onChange={() => setWriteId(cal.id)}
                          aria-label={`Write to ${cal.name}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <button type="button" disabled={calBusy} onClick={saveCalendars}>
            Save calendars
          </button>
          <button
            type="button"
            className="ghost"
            disabled={calBusy}
            onClick={disconnectGoogle}
          >
            Disconnect
          </button>
        </>
      )}
      <div className="err" style={calOk ? { color: "#c4f23c" } : undefined}>
        {calMessage}
      </div>

      <button
        type="button"
        className="ghost"
        style={{ marginTop: "2rem" }}
        onClick={signOut}
      >
        Sign out
      </button>
    </>
  );
}
